import { useState, useCallback, useEffect } from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  CardText,
  Badge,
  Button,
  Alert,
  Spinner,
} from 'reactstrap'
import { thermalPrinter } from 'utils/thermalPrinter'
import pmlogo from 'assets/images/pmlogo.png'
import { renderQrWithLogoToCanvas } from 'utils/qrLogoHelper'
import QRCode from 'react-qr-code'
import './style.scss'
import React from 'react'

const SETUP_STEPS = [
  {
    step: 1,
    title: 'Nyalakan Printer',
    description:
      'Tekan tombol power selama 2-3 detik hingga lampu indikator menyala biru.',
  },
  {
    step: 2,
    title: 'Pastikan Bluetooth Aktif',
    description:
      'Aktifkan Bluetooth pada perangkat (HP/Laptop) Anda agar siap dipasangkan.',
  },
  {
    step: 3,
    title: 'Hubungkan Perangkat',
    description:
      'Klik tombol Hubungkan di bawah, lalu pilih perangkat dengan nama "EcoPrint", "POS-58", atau "MPT-II".',
  },
  {
    step: 4,
    title: 'Cetak Uji Coba',
    description:
      'Setelah terhubung, klik tombol "Cetak Test" untuk memastikan printer berfungsi dengan baik.',
  },
]

const PrinterSetting = () => {
  const [isConnected, setIsConnected] = useState(thermalPrinter.isConnected)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [error, setError] = useState(null)
  const qrRef = React.useRef(null)
  const logoRef = React.useRef(null)

  useEffect(() => {
    const tryAutoConnect = async () => {
      setIsConnecting(true)
      const success = await thermalPrinter.autoConnect()
      setIsConnecting(false)
      if (success) {
        setIsConnected(true)
      }
    }
    tryAutoConnect()
  }, [])

  const handleConnect = async () => {
    if (!navigator.bluetooth) {
      setError('Browser Anda tidak mendukung Bluetooth. Silakan gunakan Google Chrome atau Microsoft Edge via HTTPS.')
      return
    }
    
    setIsConnecting(true)
    setError(null)
    try {
      const success = await thermalPrinter.connect()
      if (success) {
        setIsConnected(true)
      }
    } catch (err) {
      setError(err.message || 'Gagal menghubungkan printer. Pastikan Bluetooth aktif.')
    } finally {
      setIsConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    await thermalPrinter.disconnect()
    setIsConnected(false)
  }

  const handleTestPrint = async () => {
    try {
      setIsTesting(true)
      const svgElement = qrRef.current.querySelector('svg')
      const logoElement = logoRef.current

      await thermalPrinter.init()
      await thermalPrinter.center()
      
      await thermalPrinter.write(new Uint8Array([0x1b, 0x45, 0x01])) // Bold ON
      await thermalPrinter.text('PASARMIKRO TEST')
      await thermalPrinter.write(new Uint8Array([0x1b, 0x45, 0x00])) // Bold OFF
      await thermalPrinter.text(`Time: ${new Date().toLocaleTimeString('id-ID')}`)
      await thermalPrinter.feed(1)

      const canvas = await renderQrWithLogoToCanvas(svgElement, logoElement)
      if (canvas) {
        await thermalPrinter.printImage(canvas)
      } else {
        await thermalPrinter.printQR('https://pasarmikro.com/test-thermal')
      }

      await thermalPrinter.feed(4)
    } catch (err) {
      setError('Gagal mencetak. Printer mungkin terputus.')
      setIsConnected(false)
    } finally {
      setIsTesting(false)
    }
  }

  return (
    <>
      <CardBody className="printer-setting">
      {/* ─── Printer Info ─── */}
      <div className="printer-setting__info-banner">
        <div className="printer-setting__info-banner-icon">🖨️</div>
        <div>
          <h6 className="mb-1 fw-semibold">EcoPrint / POS-58</h6>
          <small className="text-muted">
            58mm Thermal Printer · Bluetooth BLE
          </small>
        </div>
        <Badge
          color={isConnected ? 'success' : 'secondary'}
          pill
          className="ms-auto printer-setting__status-badge"
          data-testid="setting-printer-status-badge"
        >
          <span
            className={`printer-setting__status-dot ${isConnected ? 'printer-setting__status-dot--active' : ''}`}
          />
          {isConnected ? 'Terhubung' : 'Terputus'}
        </Badge>
      </div>

      {error && (
        <Alert color="danger" className="mb-4 small py-2 d-flex align-items-center">
          <span className="me-2">⚠️</span>
          {error}
        </Alert>
      )}

      {/* ─── Connection Controls ─── */}
      <div className="d-flex gap-2 mb-4">
        {!isConnected ? (
          <Button
            color="primary"
            className="flex-grow-1"
            data-testid="setting-printer-connect-button"
            onClick={handleConnect}
            disabled={isConnecting}
          >
            {isConnecting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Menghubungkan...
              </>
            ) : (
              'Hubungkan Printer'
            )}
          </Button>
        ) : (
          <>
            <Button
              color="outline-primary"
              className="flex-grow-1"
              data-testid="setting-printer-test-button"
              onClick={handleTestPrint}
              disabled={isTesting}
            >
              {isTesting ? <Spinner size="sm" /> : 'Cetak Test'}
            </Button>
            <Button color="outline-danger" data-testid="setting-printer-disconnect-button" onClick={handleDisconnect}>
              Putuskan
            </Button>
          </>
        )}
      </div>

      {/* ─── Setup Guide ─── */}
      <Card className="border-0 shadow-sm mb-4">
        <CardHeader className="bg-transparent border-bottom-0 pb-0">
          <CardText tag="h5" className="fw-semibold mb-0">
            📋 Panduan Pemasangan
          </CardText>
          <small className="text-muted">
            Ikuti langkah-langkah berikut untuk menghubungkan printer
          </small>
        </CardHeader>
        <CardBody>
          <div className="printer-setting__steps">
            {SETUP_STEPS.map((item) => (
              <div key={item.step} className="printer-setting__step">
                <div className="printer-setting__step-number">{item.step}</div>
                <div className="printer-setting__step-content">
                  <h6 className="fw-semibold mb-1">{item.title}</h6>
                  <p className="text-muted mb-0 small">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <Alert color="info" className="mt-3 mb-0 small">
            <strong>💡 Tips:</strong> Jika printer tidak muncul saat mencari, pastikan printer tidak sedang terhubung ke aplikasi lain.
          </Alert>
        </CardBody>
      </Card>
      
      {/* ─── Technical Specs ─── */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="bg-transparent border-bottom-0 pb-0">
          <CardText tag="h5" className="fw-semibold mb-0">
            🔩 Spesifikasi Teknis
          </CardText>
        </CardHeader>
        <CardBody className="pt-2">
          <div className="printer-setting__specs-grid">
            <div className="printer-setting__spec-item">
              <span className="text-muted small">Lebar Kertas:</span>
              <span className="small fw-bold">58mm</span>
            </div>
            <div className="printer-setting__spec-item">
              <span className="text-muted small">Lebar Cetak:</span>
              <span className="small fw-bold">48mm</span>
            </div>
            <div className="printer-setting__spec-item">
              <span className="text-muted small">Protocol:</span>
              <span className="small fw-bold">ESC/POS</span>
            </div>
            <div className="printer-setting__spec-item">
              <span className="text-muted small">QR Mode:</span>
              <span className="small fw-bold">GS ( k</span>
            </div>
          </div>
        </CardBody>
      </Card>
    </CardBody>
      {/* Hidden elements for QR rendering */}
      <div style={{ display: 'none' }}>
        <div ref={qrRef}>
          <QRCode value="https://pasarmikro.com/test-thermal" size={256} />
        </div>
        <img ref={logoRef} src={pmlogo} alt="logo" />
      </div>
    </>
  )
}

export default PrinterSetting
